import { formatAmsterdamTime, getAmsterdamWeekday } from '@eneco/common/date';

interface OpeningHours {
  /** Opening time as `HH:mm`, inclusive. */
  open: string;
  /** Closing time as `HH:mm`, exclusive. */
  close: string;
}

type Weekday = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

/** Customer service phone hours in Amsterdam time. `null` means closed all day. */
export const CALL_SUPPORT_OPENING_HOURS: Record<Weekday, OpeningHours | null> = {
  monday: { open: '08:00', close: '21:00' },
  tuesday: { open: '08:00', close: '21:00' },
  wednesday: { open: '08:00', close: '21:00' },
  thursday: { open: '08:00', close: '21:00' },
  friday: { open: '08:00', close: '21:00' },
  saturday: null,
  sunday: null,
};

// Indexed by ISO weekday minus one, matching `getAmsterdamWeekday`.
const WEEKDAYS: Weekday[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const MINUTES_PER_DAY = 24 * 60;
const MS_PER_MINUTE = 60 * 1000;

const toMinutes = (time: string) => {
  const [hours, minutes] = time.split(':').map(Number);

  return hours * 60 + minutes;
};

const getOpeningHours = (date: Date) => CALL_SUPPORT_OPENING_HOURS[WEEKDAYS[getAmsterdamWeekday(date) - 1]];

/**
 * Whether `date` falls inside the call-support opening hours. Both weekday and time are evaluated in
 * Amsterdam time, so the result is the same for visitors in any time zone and on the server.
 */
export const isWithinCallSupportOpeningHours = (date: Date = new Date()): boolean => {
  const openingHours = getOpeningHours(date);

  if (!openingHours) return false;

  const currentMinutes = toMinutes(formatAmsterdamTime(date));

  return currentMinutes >= toMinutes(openingHours.open) && currentMinutes < toMinutes(openingHours.close);
};

/**
 * Milliseconds from `date` until the opening hours can next change: today's opening or closing time,
 * or Amsterdam midnight when neither is still ahead, after which the next day's schedule applies.
 * Daylight saving switches happen between 02:00 and 03:00, away from any of these boundaries.
 */
export const getMsUntilNextOpeningHoursBoundary = (date: Date = new Date()): number => {
  const openingHours = getOpeningHours(date);
  const currentMinutes = toMinutes(formatAmsterdamTime(date));

  const boundaries = openingHours ? [toMinutes(openingHours.open), toMinutes(openingHours.close)] : [];
  const nextBoundary = boundaries.find(boundary => boundary > currentMinutes) ?? MINUTES_PER_DAY;

  // Amsterdam is offset from UTC by whole hours, so seconds and milliseconds match local time.
  const elapsedInCurrentMinute = date.getSeconds() * 1000 + date.getMilliseconds();

  return (nextBoundary - currentMinutes) * MS_PER_MINUTE - elapsedInCurrentMinute;
};
